/**
 * Tasks API - Manejo de tareas
 */

import { callApi, apiCache, apiCallCounter } from '../apiClient';
import { logger } from '../logger';
import { DEBUG } from '../config';
import { generateLocalTimestamp } from '../taskUtils';

/**
 * Fetches tasks from the AWS Lambda function
 * @param {string} workspace - The workspace to fetch tasks for
 * @param {boolean} forceRefresh - Whether to force refresh data from API
 * @returns {Promise<Array>} - Array of tasks from DynamoDB
 */
export async function fetchTasksFromLambda(workspace = 'default', forceRefresh = false) {
  try {
    // Check if there's an in-flight request for this workspace
    if (!forceRefresh && apiCache.pendingRequests[workspace]) {
      logger.info(`Reusing in-flight request for workspace: ${workspace}`);
      return apiCache.pendingRequests[workspace];
    }
    
    // Check memory cache if not forcing refresh
    if (!forceRefresh && apiCache.tasks[workspace]) {
      logger.info(`Using cached tasks for workspace: ${workspace}`);
      return apiCache.tasks[workspace];
    }
    
    apiCallCounter.fetchTasks++;
    
    // Format the request body according to the API requirements
    const requestBody = { workspace };
    
    // Create a promise for this request and store it
    const requestPromise = (async () => {
      const data = await callApi('/tasks/read', requestBody);
      
      logger.info('Respuesta de la API:', data);
      
      // Parse the response which might have a nested JSON string in the body property
      let items = [];
      
      if (data.body && typeof data.body === 'string') {
        try {
          // Parse the stringified JSON in the body
          const parsedBody = JSON.parse(data.body);
          if (parsedBody.items && Array.isArray(parsedBody.items)) {
            items = parsedBody.items;
          }
        } catch (e) {
          logger.error('Error parsing response body:', e);
        }
      } else if (Array.isArray(data)) {
        items = data;
      } else if (data.Items && Array.isArray(data.Items)) {
        items = data.Items;
      } else if (data.items && Array.isArray(data.items)) {
        items = data.items;
      }
      
      const tasks = items.map(item => {
        // Procesar el campo url
        let processedUrl = item.url || '';
        let urlArray = [];
        
        if (Array.isArray(processedUrl)) {
          // Si ya es un array, usarlo directamente
          urlArray = processedUrl;
        } else if (typeof processedUrl === 'string') {
          // Si es un string no vacío, verificar si contiene múltiples URLs
          if (processedUrl.includes('http')) {
            if (processedUrl.includes('http', 8)) { // Si tiene más de una URL
              // Intentar separar las URLs
              const urlRegex = /(https?:\/\/[^\s]+)/g;
              urlArray = processedUrl.match(urlRegex) || [];
            } else {
              // Solo una URL
              urlArray = [processedUrl];
            }
          }
        }
        
        // Usar uuid como el identificador principal en todos los lugares
        const uuid = item.uuid || item.id;
        
        // Procesar archivos - asegurar que sea un array y loggear si hay archivos
        const filesArray = Array.isArray(item.files) ? item.files : [];
        if (filesArray.length > 0) {
          logger.info(`Tarea ${uuid} tiene ${filesArray.length} archivos: ${JSON.stringify(filesArray)}`);
        }

        return {
          uuid: uuid, // Guardar siempre el uuid
          id: uuid, // Usar el mismo valor para id, eliminando la confusión
          title: item.title || 'Sin título',
          description: item.description || '',
          assignee: item.assignee || '',
          status: item.status || 'todo',
          tags: Array.isArray(item.tags) ? item.tags : (
            item.tags ? [item.tags] : []
          ),
          comments: Array.isArray(item.comments) ? item.comments : (
            item.comments ? [item.comments] : []
          ),
          files: filesArray, // Usar el array procesado
          createdTimestamp: item.createdTimestamp || Date.now(),
          createdBy: item.createdBy || 'Usuario no identificado', // Mejor valor por defecto para tareas antiguas
          updatedAt: item.updatedAt || new Date().toLocaleDateString(),
          workspace: item.workspace || workspace,
          endDate: item.endDate || "",
          completedDate: item.completedDate || "", // Incluir la fecha de finalización
          ArchivedDate: item.ArchivedDate || false, // Incluir el campo de archivado
          url: urlArray, // Guardar las URLs como array
          activityHistory: Array.isArray(item.activityHistory) ? item.activityHistory : [] // Mantener el historial de actividad
        };
              });
      
      // Verificar si las tareas tienen historial de actividad
      if (DEBUG) {
        // Los logs de debug están desactivados
      }
      
      // Store in memory cache
      apiCache.tasks[workspace] = tasks;
      
      return tasks;
    })();
    
    // Store the pending request
    apiCache.pendingRequests[workspace] = requestPromise;
    
    // When request completes, remove it from pending
    requestPromise.finally(() => {
      delete apiCache.pendingRequests[workspace];
    });
    
    return requestPromise;
  } catch (error) {
    logger.error('Error fetching tasks from API:', error);
    throw error;
  }
}

/**
 * Sends a task to the AWS Lambda function
 * @param {Object} task - The task object to send
 * @returns {Promise<Object>} - The response from the Lambda function
 */
export async function sendTaskToLambda(task) {
  try {
    apiCallCounter.sendTask++;
    logger.important(`API Call - sendTask: ${apiCallCounter.sendTask}`);
    
    const workspace = task.workspace || 'default';
    
    // Procesar las etiquetas para asegurar que se envíen correctamente
    let processedTags = [];
    if (task.tags) {
      if (Array.isArray(task.tags)) {
        // Limpiar etiquetas y manejar posibles cadenas con comas
        processedTags = task.tags
          .filter(tag => tag && typeof tag === 'string')
          .flatMap(tag => 
            tag.includes(',') ? tag.split(',').map(t => t.trim().toUpperCase()).filter(Boolean) : tag.trim().toUpperCase()
          )
          .filter(Boolean);
      } else if (typeof task.tags === 'string') {
        processedTags = task.tags.split(',').map(tag => tag.trim().toUpperCase()).filter(Boolean);
      }
    }
    
    // Format the task data according to what the new lambda expects
    const taskData = {
      title: task.title,
      description: task.description || '',
      assignee: task.assignee || '',
      status: task.status || 'todo',
      tags: processedTags, // Usar las etiquetas procesadas
      comments: task.comments || [],
      workspace: workspace,
      files: task.files || [], // Asegurar que files sea siempre un array
      url: Array.isArray(task.url) ? task.url : (task.url || ''), // Guardar array de URLs en campo url
      createdBy: task.createdBy, // Campo requerido para el nuevo lambda
      endDate: task.endDate || '', // Incluir la fecha de vencimiento
      completedDate: task.completedDate || '', // Incluir la fecha de finalización
      ArchivedDate: task.ArchivedDate || false, // Incluir el campo de archivado
      activityHistory: task.activityHistory || [] // Incluir el historial de actividad
    };
    
    // Mantenemos el campo id si existe (ahora que usamos uuid como id)
    if (task.id) {
      taskData.id = task.id;
    }
    
    const requestBody = taskData;
    
    const data = await callApi('/tasks/create', requestBody);
    
    // Invalidate cache for this workspace to ensure fresh data on next fetch
    delete apiCache.tasks[workspace];
    
    return data;
  } catch (error) {
    logger.error('Error sending task to API:', error);
    throw error;
  }
}

/**
 * Updates a task using the edit-card Lambda function
 * @param {string} uuid - The UUID of the task to update
 * @param {Object} updates - Object containing the fields to update
 * @returns {Promise<Object>} - The response from the Lambda function
 */
export async function updateTaskInLambda(uuid, updates) {
  try {
    apiCallCounter.updateTask++;
    logger.important(`API Call - updateTask: ${apiCallCounter.updateTask}`);
    
    if (!uuid) {
      throw new Error('Se requiere un UUID válido para actualizar la tarea');
    }
    
    if (!updates || typeof updates !== 'object') {
      throw new Error('Se requiere un objeto de actualizaciones válido');
    }
    
    // Crear una copia del objeto updates para modificarlo
    const updatesClone = { ...updates };
    
    // Asegurarse de que no se intente actualizar el campo uuid, que es clave primaria
    if (updatesClone.uuid) {
      delete updatesClone.uuid;
    }
    
    // Asegurarnos de que el campo activityHistory se envíe correctamente
    if (updatesClone.activityHistory) {
      // Validar que el campo sea un array
      if (!Array.isArray(updatesClone.activityHistory)) {
        console.error('El campo activityHistory no es un array:', updatesClone.activityHistory);
        // Convertir a array si es posible, o crear uno vacío
        updatesClone.activityHistory = Array.isArray(updatesClone.activityHistory) 
          ? updatesClone.activityHistory 
          : [];
      }
      
      if (DEBUG) {
        // Los logs de debug están desactivados
      }
    }
    
    // Procesar urls si existen en las actualizaciones
    if (updatesClone.url) {
      // Si es array, mantenerlo así
      if (Array.isArray(updatesClone.url)) {
        logger.info('URLs en array se envían directamente:', updatesClone.url);
      } else if (typeof updatesClone.url === 'string') {
        // Si es un string, convertir a array
        logger.info('URL string convertida a array:', updatesClone.url);
        updatesClone.url = [updatesClone.url];
      }
    }
    
    // Asegurarnos de que el campo completedDate se envíe correctamente cuando existe
    // Si status es 'done' y no hay completedDate, establecerlo
    if (updatesClone.status === 'done' && (!updatesClone.completedDate || (typeof updatesClone.completedDate === 'string' && updatesClone.completedDate.trim() === ''))) {
      updatesClone.completedDate = generateLocalTimestamp();
      logger.info(`Estableciendo fecha de finalización automáticamente: ${updatesClone.completedDate}`);
    }
    
    // Manejar completedDate cuando existe
    if (updatesClone.completedDate !== undefined) {
      // Si es string vacío o solo espacios, no enviarlo (evitar "<empty>" en DynamoDB)
      if (typeof updatesClone.completedDate === 'string' && updatesClone.completedDate.trim() === '') {
        // Si está vacío y el status NO es 'done', no incluirlo en los updates
        if (updatesClone.status !== 'done') {
          delete updatesClone.completedDate;
        } else {
          // Si está vacío pero el status es 'done', establecerlo
          updatesClone.completedDate = generateLocalTimestamp();
        }
      } else if (updatesClone.completedDate) {
        // Si tiene un valor válido, asegurarnos de que sea string válido
        logger.info(`Enviando fecha de finalización: ${updatesClone.completedDate}`);
        updatesClone.completedDate = String(updatesClone.completedDate).trim();
      }
    }
    
    // Asegurarnos de que el campo ArchivedDate se envíe correctamente cuando existe
    if (updatesClone.ArchivedDate !== undefined) {
      logger.info(`Enviando estado de archivado: ${updatesClone.ArchivedDate}`);
      // Nos aseguramos de que este campo siempre se incluya y no se filtre
      if (typeof updatesClone.ArchivedDate === 'boolean') {
        // Mantener el valor booleano tal como está
      } else {
        // Si no es booleano, convertir a booleano
        updatesClone.ArchivedDate = Boolean(updatesClone.ArchivedDate);
      }
    }
    
    // Asegurarnos de que el campo files se envíe correctamente cuando existe
    if (updatesClone.files) {
      logger.info(`Enviando archivos: ${JSON.stringify(updatesClone.files)}`);
      // Validar que sea un array
      if (!Array.isArray(updatesClone.files)) {
        logger.error('El campo files no es un array:', updatesClone.files);
        updatesClone.files = Array.isArray(updatesClone.files) ? updatesClone.files : [];
      }
    }
    
    // Format the request body according to the API requirements
    const requestBody = {
      uuid: uuid,
      updates: updatesClone
    };

    const data = await callApi('/tasks/update', requestBody);
    
    // Invalidate cache for all workspaces - we don't know which workspace this task belongs to
    Object.keys(apiCache.tasks).forEach(key => {
      delete apiCache.tasks[key];
    });
    
    return data;
  } catch (error) {
    logger.error('Error updating task in Lambda:', error);
    throw error;
  }
}

/**
 * Deletes a task using the delete Lambda function
 * @param {string} uuid - The UUID of the task to delete
 * @param {string} deletedBy - Email of the user who deleted the task
 * @returns {Promise<Object>} - The response from the Lambda function
 */
export async function deleteTaskFromLambda(uuid, deletedBy) {
  try {
    apiCallCounter.deleteTask++;
    logger.important(`API Call - deleteTask: ${apiCallCounter.deleteTask}`);
    
    if (!uuid) {
      throw new Error('Se requiere un UUID válido para eliminar la tarea');
    }
    
    if (!deletedBy) {
      throw new Error('Se requiere especificar quién eliminó la tarea');
    }
    
    // Validate UUID format (basic check - should be a non-empty string)
    if (typeof uuid !== 'string' || uuid.trim() === '') {
      throw new Error(`UUID inválido: "${uuid}"`);
    }
    
    logger.info(`Marcando tarea con UUID ${uuid} como eliminada por ${deletedBy}`);
    
    // Format the request body according to the API requirements
    const requestBody = { 
      uuid: uuid.trim(),
      deletedBy: deletedBy
    };
    
    // Log request details for debugging
    logger.info(`Delete task request body:`, JSON.stringify(requestBody));
    
    try {
      const data = await callApi('/tasks/delete', requestBody);
      
      // Invalidate all workspace caches since we don't know which workspace this task belongs to
      apiCache.tasks = {};
      
      return data;
    } catch (apiError) {
      // Enhanced error logging with more details
      logger.error('Error en llamada a API /tasks/delete:', {
        uuid: uuid,
        deletedBy: deletedBy,
        error: apiError.message,
        status: apiError.status,
        statusText: apiError.statusText,
        responseData: apiError.responseData,
        responseText: apiError.responseText
      });
      
      // Re-throw with enhanced context
      const enhancedError = new Error(
        apiError.message || `Error al eliminar tarea: ${apiError.status ? `HTTP ${apiError.status}` : 'Error desconocido'}`
      );
      enhancedError.status = apiError.status;
      enhancedError.responseData = apiError.responseData;
      enhancedError.responseText = apiError.responseText;
      throw enhancedError;
    }
  } catch (error) {
    // Final error logging
    logger.error('Error marcando tarea como eliminada en Lambda:', {
      uuid: uuid,
      deletedBy: deletedBy,
      error: error.message,
      status: error.status,
      stack: error.stack
    });
    throw error;
  }
} 